import React, { useEffect, useMemo, useState } from 'react';
import { Sun, Moon, Search } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DataTable } from '../../components/ds';
import { CATALOG } from './componentCatalog.data';
import {
  TIER_ORDER,
  TIER_META,
  type CatalogEntry,
  type CatalogStatus,
  type PropDoc,
} from './componentCatalog.types';

/**
 * Hybrid atomic-design component catalog.
 *
 * This page is a TOP-LEVEL route (not under the pinned-dark design-language shell) because it
 * must show real components in BOTH modes. Real components use Tailwind `dark:` variants, which
 * key off the `.dark` class on <html> — so the catalog owns that class while mounted and restores
 * it on unmount (the same pattern PrintChrome / ThemeContext.setForcedLight use). Flipping the
 * real global class is what makes the light/dark preview honest for live specimens.
 *
 * ── Why entries carry props tables ─────────────────────────────────────────
 * A catalog that shows one specimen per component teaches callers that the
 * component has one appearance — and they go rewrite the markup to get a second
 * one. That is the drift the ds/ layer exists to stop, so the PARAMETERS are
 * documented as first-class content: a props table and as many named examples
 * as it takes to prove what each parameter does. The props table itself is a
 * DataTable, which means the catalog cannot document the component without
 * also exercising it.
 */

/**
 * Status pills.
 *
 * These used to hardcode their fill and border as rgba of the DARK palette —
 * rgba(58,125,86,…) is #3A7D56, night green — while their ink came from a
 * token. Under the daylight ramp the text moved and the fill did not, so a pill
 * read as forest ink on a night-green plate. Staging this page is what surfaced
 * it (conflicts №01–03).
 *
 * Fill now uses the -dim tokens, which remap per surface. Border colour is an
 * INLINE STYLE, not a class: `.dark * { border-color: var(--rule) }` in
 * index.css comes after Tailwind's plain utilities and wins the tie, so a
 * bordered token colour set by a plain class flattens to grey in dark mode.
 * See /design-language/foundations.
 */
const STATUS_PILL: Record<CatalogStatus, { label: string; cls: string; border: string }> = {
  live: {
    label: 'Live',
    cls: 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]',
    border: 'var(--green)',
  },
  documented: {
    label: 'Documented',
    cls: 'text-[var(--text-ter)] bg-[var(--surface-raised)]',
    border: 'var(--rule-bright)',
  },
  'needs-work': {
    label: 'Needs work',
    cls: 'text-[var(--coral)] bg-[var(--coral-dim)]',
    border: 'var(--coral)',
  },
  gap: {
    label: 'Gap',
    cls: 'text-[var(--mustard)] bg-transparent',
    border: 'var(--mustard)',
  },
};

const StatusPill: React.FC<{ status: CatalogStatus }> = ({ status }) => {
  const s = STATUS_PILL[status];
  return (
    <span
      className={`shrink-0 inline-flex items-center border px-2 py-0.5 font-mono text-[13px] uppercase tracking-wider ${s.cls}`}
      style={{ borderColor: s.border }}
    >
      {s.label}
    </span>
  );
};

const Stage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap items-center gap-4 bg-[var(--bg-raised)] p-6">{children}</div>
);

const PROP_COLUMNS = [
  {
    key: 'name',
    header: 'Prop',
    width: '30%',
    render: (p: PropDoc) => (
      <span className="font-mono text-[13px] text-[var(--text-pri)]">
        {p.name}
        {p.required && <span className="ml-1 text-[var(--coral)]" title="Required">*</span>}
      </span>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    width: '26%',
    render: (p: PropDoc) => <span className="font-mono text-[13px] text-[var(--mustard)]">{p.type}</span>,
  },
  {
    key: 'default',
    header: 'Default',
    width: '12%',
    render: (p: PropDoc) =>
      p.default ? (
        <span className="font-mono text-[13px] text-[var(--text-ter)]">{p.default}</span>
      ) : (
        <span className="text-[var(--text-dim)]">—</span>
      ),
  },
  { key: 'description', header: 'Description', render: (p: PropDoc) => p.description },
];

const Specimen: React.FC<{ entry: CatalogEntry }> = ({ entry }) => (
  <div className="flex flex-col overflow-hidden border border-[var(--rule)] bg-[var(--surface)]">
    <div className="border-b border-[var(--rule)] px-5 pb-4 pt-5">
      <div className="mb-1.5 flex items-start justify-between gap-3">
        <h3 className="font-serif font-semibold leading-tight text-[var(--text-pri)]">{entry.name}</h3>
        <StatusPill status={entry.status} />
      </div>
      <p className="text-sm leading-relaxed text-[var(--text-sec)]">{entry.description}</p>
      <code className="mt-2 inline-block break-all font-mono text-[13px] text-[var(--mustard)]">{entry.importPath}</code>
    </div>

    {entry.render ? (
      <Stage>{entry.render()}</Stage>
    ) : (
      <div className="flex-1 bg-[var(--bg-raised)] p-6 text-sm leading-relaxed text-[var(--text-ter)]">
        <span className="mb-1 block font-mono text-[13px] uppercase tracking-wider text-[var(--text-dim)]">
          {entry.status === 'gap' ? 'Not built yet' : 'Not rendered here'}
        </span>
        {entry.note}
      </div>
    )}

    {entry.examples?.map((ex) => (
      <div key={ex.name} className="border-t border-[var(--rule)]">
        <div className="px-5 pb-3 pt-4">
          <p className="font-mono text-[13px] tracking-[0.04em] text-[var(--text-pri)]">{ex.name}</p>
          {ex.description && (
            <p className="mt-1 max-w-[62ch] text-[13px] leading-relaxed text-[var(--text-ter)]">{ex.description}</p>
          )}
        </div>
        <Stage>{ex.render()}</Stage>
      </div>
    ))}

    {entry.usage && (
      <div className="border-t border-[var(--rule)] px-5 py-4">
        <p className="mb-2 font-mono text-[13px] uppercase tracking-wider text-[var(--text-dim)]">Usage</p>
        <pre className="overflow-x-auto bg-[var(--bg-raised)] p-3 font-mono text-[13px] leading-relaxed text-[var(--text-sec)]">
          {entry.usage}
        </pre>
      </div>
    )}

    {entry.props && entry.props.length > 0 && (
      <div className="border-t border-[var(--rule)] px-5 py-4">
        <p className="mb-2 font-mono text-[13px] uppercase tracking-wider text-[var(--text-dim)]">
          Props · <span className="text-[var(--coral)]">*</span> required
        </p>
        <DataTable
          columns={PROP_COLUMNS}
          rows={entry.props}
          getRowKey={(p) => p.name}
          density="compact"
          bordered={false}
          caption={`${entry.name} props`}
          captionHidden
        />
      </div>
    )}
  </div>
);

export const PrioritiesAIComponentCatalogPage: React.FC = () => {
  const [mode, setMode] = useState<'dark' | 'light'>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
  const [query, setQuery] = useState('');

  // Apply the current preview mode to <html> (the class every dark: variant keys off).
  useEffect(() => {
    const html = document.documentElement;
    if (mode === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
  }, [mode]);

  // Restore the app's original theme when leaving the catalog.
  useEffect(() => {
    const html = document.documentElement;
    const original = html.classList.contains('dark');
    return () => {
      if (original) html.classList.add('dark');
      else html.classList.remove('dark');
    };
  }, []);

  // Match on name, description and prop names, so "showHeader" finds DataTable.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATALOG;
    return CATALOG.filter((e) =>
      [e.name, e.description, e.importPath, ...(e.props?.map((p) => p.name) ?? [])]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [query]);

  return (
    /* STAGE 1 of the daylight adoption (conflicts №01/02/03, owner ratified
       2026-08-30, staged). `daylight-surface` remaps the product tokens beneath
       it; every ds/ component on this page therefore renders in the daylight
       ramp without a single component changing. This page first because it
       shows all of them at once — the most information from one screen, and
       reverting is deleting one class name. */
    <div className="daylight-surface min-h-[calc(100vh-52px)] w-full bg-[var(--bg)] font-body text-[var(--text-pri)]">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <ProductDesignPageHeader
          title="Component catalog"
          eyebrow="Product lane · atomic design"
          subtitle="Live specimens of the real coded components, organized atoms → molecules → organisms. Parameterized components carry a props table and one named example per parameter. Entries that need a provider, context, or data are documented with their import path; genuinely missing primitives are marked as gaps."
          actions={
            <button
              type="button"
              onClick={() => setMode((m) => (m === 'dark' ? 'light' : 'dark'))}
              aria-pressed={mode === 'light'}
              className="inline-flex items-center gap-2 border border-[var(--rule-bright)] px-3 py-1.5 font-mono text-[13px] uppercase tracking-[0.12em] text-[var(--text-sec)] transition-colors hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]"
            >
              {mode === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              Preview {mode === 'dark' ? 'light' : 'dark'} mode
            </button>
          }
        />

        <p className="mb-6 max-w-3xl border border-[var(--rule)] bg-[var(--surface)] px-4 py-3 text-sm leading-relaxed text-[var(--text-ter)]">
          The preview toggle flips the app's real theme while you're on this page (that's how the
          live specimens re-theme accurately) — it restores your previous theme when you leave.
        </p>

        <div className="mb-8 flex flex-wrap items-center gap-4">
          <label className="relative flex-1 basis-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-dim)]" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a component or a prop — try showHeader"
              aria-label="Filter the catalog"
              className="h-9 w-full border border-[var(--rule-bright)] bg-[var(--surface)] pl-9 pr-3 text-sm text-[var(--text-pri)] placeholder:text-[var(--text-dim)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--mustard)]"
            />
          </label>
          <span className="font-mono text-[13px] tracking-[0.04em] text-[var(--text-dim)]">
            {visible.length} of {CATALOG.length}
          </span>
          <div className="flex flex-wrap gap-3">
            {(Object.keys(STATUS_PILL) as CatalogStatus[]).map((s) => (
              <span key={s} className="inline-flex items-center gap-2 text-xs text-[var(--text-sec)]">
                <StatusPill status={s} />
                <span className="text-[var(--text-ter)]">
                  {s === 'live' && 'rendered from source'}
                  {s === 'documented' && 'exists — needs provider/context'}
                  {s === 'needs-work' && 'exists — off-spec'}
                  {s === 'gap' && 'not built yet'}
                </span>
              </span>
            ))}
          </div>
        </div>

        {visible.length === 0 && (
          <p className="py-16 text-center text-sm text-[var(--text-ter)]">
            Nothing matches “{query}”.
          </p>
        )}

        {TIER_ORDER.map((tier) => {
          const entries = visible.filter((e) => e.tier === tier);
          if (entries.length === 0) return null;
          return (
            <ProductDesignSection key={tier} title={TIER_META[tier].label} hint={TIER_META[tier].blurb}>
              <div className="grid grid-cols-1 gap-6">
                {entries.map((entry) => (
                  <Specimen key={entry.id} entry={entry} />
                ))}
              </div>
            </ProductDesignSection>
          );
        })}
      </div>
    </div>
  );
};
